import React from 'react';
import { OptionType, Select, styled, Text } from '@cx/ui';
import AddFilterButton from 'components/AddFilterButton';
import RemoveFilterButton from 'components/RemoveFilterButton';
import { PartitionOptionType } from '../types';
import usePartition from '../hooks/usePartition';
import DataDrivenFilterDimension from './DataDrivenFiltersDimension';

type DataDrivenFilterProps = {
  index: number;
  partitionOptions: PartitionOptionType[];
  selectedPartition?: PartitionOptionType;
  maxDimensions?: number;
  onRemoveSource: (index: number) => void;
  onSourceSelected: (option: OptionType, index: number) => void;
} & Pick<React.HTMLAttributes<HTMLDivElement>, 'className'>;

/**
 * Compent in charge of rendering the partition of the enterprise filter.
 * when an user add a new partition, this component will be rendered and let you select a partition and add filters to it.
 * the user can only select a partition once, once a partition is added the user can not change it and it will be removed from the partition options.
 * If the user remove the partition, it will be added back to the partition options.
 * @param param0
 * @returns
 */
function DataDrivenFilterPartition({
  partitionOptions,
  className,
  index,
  selectedPartition,
  maxDimensions,
  onRemoveSource,
  onSourceSelected,
}: DataDrivenFilterProps) {
  const {
    onAddOption,
    onOptionSelected,
    selectedFilters,
    dimensionOptions,
    onRemoveOption,
  } = usePartition({
    selectedPartitionIndex: index,
  });

  const handleRemoveSource = () => onRemoveSource(index);

  const handleSourceSelected = (option: OptionType) => {
    if (selectedPartition?.ui?.loading) return;
    onSourceSelected(option, index);
  };

  return (
    <div className={className}>
      <div className="filter-section-container">
        {selectedPartition?.data.partition ? (
          <Text className="selected-source-title">
            {selectedPartition?.data.partition?.Description}
          </Text>
        ) : (
          <Select
            name="enterprise-filter-selector"
            placeholder="Select a partition"
            className="select"
            options={partitionOptions}
            onChange={handleSourceSelected}
            maxMenuHeight={250}
          />
        )}
        <RemoveFilterButton onClick={handleRemoveSource} />
      </div>
      {selectedPartition?.data.partition ? (
        <div className="filter-Option-container">
          {selectedFilters.map((filter, i) => (
            <div key={filter.data.id} className="filter-section-container">
              <DataDrivenFilterDimension
                selectedFilterIndex={i}
                selectedPartitionIndex={index}
                dimensions={dimensionOptions}
                isLoading={selectedPartition?.ui?.loading}
                selectedFilter={filter}
                onDimensionSelected={onOptionSelected}
              />
              <RemoveFilterButton onClick={() => onRemoveOption(filter, i)} />
            </div>
          ))}
          {selectedPartition && selectedFilters.length < maxDimensions ? (
            <div className="add-dimension-partition-container">
              <AddFilterButton
                label="Add dimension to partition"
                onClick={onAddOption}
                selectedFilters={selectedFilters}
              />
            </div>
          ) : null}

          {selectedFilters.length >= maxDimensions ? (
            <Text
              data-testid="cx-data-driven-filters-feedback-message"
              className="cx-data-driven-filters-feedback-message cx2-lang"
            >
              No more than {maxDimensions} dimensions can be added at once
            </Text>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default styled(DataDrivenFilterPartition)((props) => ({
  paddingTop: props.theme.spacing(1),
  ...props.theme.typography.smallMedium,
  borderBottom: `1px solid ${props.theme.palette.onBackground.light}`,
  '.filter-section-container': {
    display: 'grid',
    gridTemplateColumns: `calc(100% - ${props.theme.spacing(1)}) ${props.theme.spacing(1)} `,
    gap: props.theme.spacing(0.5),
    marginBottom: props.theme.spacing(1),
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  '.filter-Option-container': {
    padding: props.theme.spacing(1, 0),
    marginLeft: props.theme.spacing(1),
  },
  '.selected-source-title': {
    ...props.theme.typography.smallMedium,
  },
}));
